import { Temporal } from '@js-temporal/polyfill'

import type { Service } from '~publish/contexts/AccountContext/graphqlEnums'
import { endOfDay, startOfWeek } from '~publish/helpers/temporal'
import { getValueLedPreviewServices } from '~publish/helpers/valueLedEmptyStates'

const DEFAULT_PREVIEW_SLOTS_PER_WEEK = 3
const PREVIEW_HOURS: number[] = [9, 13, 17]

export type PreviewCalendarSlot = {
  date: string
  id: string
  channelId: null
  channelName: string
  channelService: Service
}

function getDistributedIndexes(length: number, count: number): number[] {
  const selectedCount = Math.min(length, count)
  if (selectedCount <= 0) return []

  return Array.from({ length: selectedCount }, (_, index) =>
    Math.floor(((index + 0.5) * length) / selectedCount),
  )
}

export function buildPreviewCalendarSlots({
  onboardingServices,
  timezone,
  weekStartsOn = 0,
  repeatEachWeek = true,
  startTimestamp,
  endDate,
  now = Date.now(),
}: {
  onboardingServices: Service[] | null
  timezone: string
  weekStartsOn?: 0 | 1
  repeatEachWeek?: boolean
  startTimestamp: number
  endDate: string
  now?: number
}): PreviewCalendarSlot[] {
  const services = getValueLedPreviewServices(onboardingServices)
  const rangeStart = Temporal.Instant.fromEpochMilliseconds(
    Math.max(startTimestamp, now),
  ).toZonedDateTimeISO(timezone)
  const rangeEnd = Temporal.Instant.from(endDate).toZonedDateTimeISO(timezone)
  const slots: PreviewCalendarSlot[] = []
  let weekStart = repeatEachWeek
    ? startOfWeek(rangeStart, { weekStartsOn })
    : rangeStart.startOfDay()
  let weekIndex = 0

  while (Temporal.ZonedDateTime.compare(weekStart, rangeEnd) <= 0) {
    const availableDays: Temporal.ZonedDateTime[] = []
    const segmentEnd = weekStart.add({ days: repeatEachWeek ? 7 : 3 })
    let candidateDay = weekStart
    while (
      Temporal.ZonedDateTime.compare(candidateDay, rangeEnd) <= 0 &&
      Temporal.ZonedDateTime.compare(candidateDay, segmentEnd) < 0
    ) {
      const isWithinRange =
        Temporal.ZonedDateTime.compare(endOfDay(candidateDay), rangeStart) >=
          0 && Temporal.ZonedDateTime.compare(candidateDay, rangeEnd) <= 0
      const hasAvailableTime = PREVIEW_HOURS.some(
        (hour) =>
          Temporal.ZonedDateTime.compare(
            candidateDay.with({ hour }),
            rangeStart,
          ) > 0,
      )
      if (isWithinRange && hasAvailableTime) availableDays.push(candidateDay)
      candidateDay = candidateDay.add({ days: 1 })
    }
    const selectedDays = getDistributedIndexes(
      availableDays.length,
      DEFAULT_PREVIEW_SLOTS_PER_WEEK,
    ).map((index) => availableDays[index])

    selectedDays.forEach((selectedDay, slotIndex) => {
      const serviceIndex =
        (weekIndex * DEFAULT_PREVIEW_SLOTS_PER_WEEK + slotIndex) %
        services.length
      const service = services[serviceIndex]
      const preferredHour = PREVIEW_HOURS[serviceIndex % PREVIEW_HOURS.length]
      const availableHours = [
        preferredHour,
        ...PREVIEW_HOURS.filter((hour) => hour !== preferredHour),
      ]
      const hour = availableHours.find(
        (candidateHour) =>
          Temporal.ZonedDateTime.compare(
            selectedDay.with({ hour: candidateHour }),
            rangeStart,
          ) > 0,
      )
      if (hour === undefined) return

      const date = selectedDay.with({ hour }).toInstant().toString()
      if (Temporal.Instant.from(date).since(rangeEnd.toInstant()).sign > 0) {
        return
      }

      slots.push({
        date,
        id: `preview:${service}:${date}`,
        channelId: null,
        channelName: service,
        channelService: service,
      })
    })

    if (!repeatEachWeek) break
    weekStart = weekStart.add({ weeks: 1 })
    weekIndex++
  }

  return slots.toSorted((a, b) => a.date.localeCompare(b.date))
}
